import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Link } from 'react-router-dom';
import api from '../services/api';
import MapFilters from '../components/map/MapFilters';

// ===== SVG Markers =====
const createMarkerSVG = (color: string, icon: string) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
      <defs>
        <filter id="shadow" x="-20%" y="-10%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
        </filter>
      </defs>
      <path d="M20 50 C20 50 2 30 2 18 C2 8 10 1 20 1 C30 1 38 8 38 18 C38 30 20 50 20 50Z" 
            fill="${color}" stroke="white" stroke-width="2" filter="url(#shadow)"/>
      <circle cx="20" cy="18" r="12" fill="white" opacity="0.9"/>
      <text x="20" y="23" text-anchor="middle" font-size="16">${icon}</text>
    </svg>`;
  return L.divIcon({
    html: svg,
    className: '',
    iconSize: [40, 52],
    iconAnchor: [20, 52],
    popupAnchor: [0, -52],
  });
};

const livestockIcon = createMarkerSVG('#16a34a', '🐑');
const servicesIcon = createMarkerSVG('#2563eb', '🔧');
const bothIcon = createMarkerSVG('#7c3aed', '⭐');
const defaultIcon = createMarkerSVG('#6b7280', '📍');

const getSellerIcon = (sellerType: string | null) => {
  switch (sellerType) {
    case 'livestock': return livestockIcon;
    case 'services': return servicesIcon;
    case 'both': return bothIcon;
    default: return defaultIcon;
  }
};

// ===== Seller Tooltip Card =====
interface SellerCardData {
  user: {
    id: number;
    name: string;
    avatar: string | null;
    account_type: string;
    seller_type: string | null;
    is_verified: boolean;
    city: { id: number; name_ar: string } | null;
    rating_avg: string;
    rating_count: number;
    ads_count: number;
    categories: { id: number; name_ar: string; slug: string }[];
    services: { id: number; name_ar: string }[];
  };
  recent_ads: {
    id: number;
    title: string;
    price: string;
    images: { image_url: string; thumbnail_url: string | null }[];
  }[];
}

const SellerTooltip: React.FC<{ data: SellerCardData; onClose: () => void }> = ({ data, onClose }) => {
  const { user, recent_ads } = data;
  return (
    <div className="bg-white rounded-xl shadow-xl w-72 overflow-hidden border border-gray-100" dir="rtl">
      {/* Header */}
      <div className="p-3 bg-gray-50 border-b">
        <div className="flex justify-between items-start">
          <Link to={`/users/${user.id}`} className="flex gap-2.5 items-center hover:opacity-80 transition">
            <div className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-primary-500 text-lg font-bold">{user.name.charAt(0)}</span>
              )}
            </div>
            <div>
              <div className="flex items-center gap-1">
                <h3 className="font-bold text-gray-800 text-sm">{user.name}</h3>
                {user.is_verified && <span className="text-blue-500 text-xs">✓</span>}
              </div>
              <p className="text-xs text-gray-400">{user.city?.name_ar}</p>
            </div>
          </Link>
          <button onClick={onClose} className="text-gray-300 hover:text-gray-500 text-sm leading-none p-1">✕</button>
        </div>

        {/* التقييم والعدد */}
        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
          <span className="flex items-center gap-0.5">
            <span className="text-yellow-400">★</span> {parseFloat(user.rating_avg).toFixed(1)}
            <span className="text-gray-300 mr-0.5">({user.rating_count})</span>
          </span>
          <span>{user.ads_count} إعلان</span>
        </div>

        {/* التصنيفات */}
        {user.categories.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {user.categories.map((cat) => (
              <span key={cat.id} className="bg-green-50 text-green-600 text-[10px] px-1.5 py-0.5 rounded-full">
                {cat.name_ar}
              </span>
            ))}
            {user.services.map((srv) => (
              <span key={srv.id} className="bg-blue-50 text-blue-600 text-[10px] px-1.5 py-0.5 rounded-full">
                {srv.name_ar}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* آخر الإعلانات */}
      {recent_ads.length > 0 && (
        <div className="p-2.5">
          <p className="text-[10px] text-gray-400 font-semibold mb-1.5">آخر الإعلانات</p>
          <div className="space-y-1.5">
            {recent_ads.slice(0, 3).map((ad) => (
              <Link
                key={ad.id}
                to={`/ads/${ad.id}`}
                className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-50 transition"
              >
                <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                  {ad.images?.[0] ? (
                    <img src={ad.images[0].thumbnail_url || ad.images[0].image_url} alt={ad.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300 text-[10px]">📷</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-700 truncate">{ad.title}</p>
                  <p className="text-[11px] text-primary-500 font-semibold">{parseFloat(ad.price).toLocaleString()} ر.س</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* زر الملف الشخصي */}
      <div className="px-2.5 pb-2.5">
        <Link
          to={`/users/${user.id}`}
          className="block w-full text-center bg-primary-500 text-white py-2 rounded-lg text-xs font-semibold hover:bg-primary-600 transition"
        >
          عرض الملف الشخصي
        </Link>
      </div>
    </div>
  );
};

// ===== Map Center Handler =====
const MapCenterHandler: React.FC<{ center: [number, number] }> = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
};

// ===== Seller Marker with Tooltip =====
interface Seller {
  id: number;
  name: string;
  avatar: string | null;
  account_type: string;
  seller_type: string | null;
  latitude: number;
  longitude: number;
  is_verified: boolean;
  rating_avg: string;
  ads_count: number;
  city: string;
  categories: { id: number; name_ar: string; slug: string }[];
  services: { id: number; name_ar: string }[];
}

// ===== Main Page =====
const HomePage: React.FC = () => {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [selectedSellerData, setSelectedSellerData] = useState<SellerCardData | null>(null);
  const [selectedSellerPos, setSelectedSellerPos] = useState<{ lat: number; lng: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [mapCenter] = useState<[number, number]>([24.7136, 46.6753]);
  const [filters, setFilters] = useState({ category_id: '', region_id: '' });

  useEffect(() => {
    fetchSellers();
  }, [filters]);

  const fetchSellers = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filters.category_id) params.category_id = filters.category_id;
      if (filters.region_id) params.region_id = filters.region_id;
      const res = await api.get('/map/sellers', { params });
      setSellers(res.data.data || []);
    } catch (err) {
      console.error('Error fetching sellers:', err);
      setSellers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkerClick = async (seller: Seller) => {
    try {
      setSelectedSellerPos({ lat: seller.latitude, lng: seller.longitude });
      const res = await api.get(`/map/sellers/${seller.id}`);
      setSelectedSellerData(res.data.data);
    } catch (err) {
      console.error('Error fetching seller card:', err);
    }
  };

  const handleFilterChange = (newFilters: { category_id: string; region_id: string }) => {
    setFilters(newFilters);
    setSelectedSellerData(null);
    setSelectedSellerPos(null);
  };

  const closeTooltip = () => {
    setSelectedSellerData(null);
    setSelectedSellerPos(null);
  };

  return (
    <div className="relative" style={{ height: 'calc(100vh - 130px)' }}>
      <MapFilters onFilterChange={handleFilterChange} />

      <MapContainer
        center={mapCenter}
        zoom={6}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapCenterHandler center={mapCenter} />

        {sellers.map((seller) => (
          <Marker
            key={seller.id}
            position={[seller.latitude, seller.longitude]}
            icon={getSellerIcon(seller.seller_type)}
            eventHandlers={{
              click: () => handleMarkerClick(seller),
            }}
          />
        ))}
      </MapContainer>

      {/* Loading */}
      {loading && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 bg-white px-4 py-2 rounded-lg shadow-md z-[1000]">
          <span className="text-sm text-gray-600">جاري تحميل البيانات...</span>
        </div>
      )}

      {/* عدد النتائج + Legend */}
      {!loading && (
        <div className="absolute bottom-4 left-4 bg-white px-3 py-2 rounded-lg shadow-md z-[1000]">
          <span className="text-sm text-gray-600 block">{sellers.length} بائع/مقدم خدمة</span>
          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-400">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block"></span> مواشي</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block"></span> خدمات</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-purple-500 inline-block"></span> الكل</span>
          </div>
        </div>
      )}

      {/* Seller Tooltip */}
      {selectedSellerData && selectedSellerPos && (
        <div
          className="absolute z-[1001]"
          style={{ top: '50%', left: '50%', transform: 'translate(-50%, -60%)' }}
        >
          <SellerTooltip data={selectedSellerData} onClose={closeTooltip} />
        </div>
      )}
    </div>
  );
};

export default HomePage;
